import { useMemo, useState } from 'react'
import {
	ArrowLeftIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	XMarkIcon
} from '@heroicons/react/24/outline'
import {
	addDays,
	addMonths,
	endOfMonth,
	format,
	getDay,
	getMonth,
	getYear,
	isSameDay,
	isToday,
	startOfMonth,
	subMonths
} from 'date-fns'
import { enGB, is } from 'date-fns/locale'
import { useTranslation } from 'libs/i18n'
import { Link } from 'libs/router'
import { CtaLink, Dialog, Header } from 'shared/components'
import { Badge, IconButton } from 'shared/atoms'
import { useModal, useUserRoles } from 'shared/hooks'
import { routes } from 'config/routes'
import { cn } from 'utils/style'
import type { BookingFormattedInfo } from './types'
import { BookingStatus } from '../../molecules/BookingStatus/BookingStatus'
import { useFetchBookings } from './queries/useFetchBookings'
import { usePopulateCalendar } from './hooks/usePopulateCalendar'
import { useSetQuery } from './queries/useSetQuery'

type DayCounts = {
	date: Date
	starting: number
	ending: number
	takedown: number
}

const legendDot = 'inline-block h-2 w-2 shrink-0 rounded-full'
const tagClassName = 'px-2 py-0.5 font-bold uppercase tracking-wide'

const BookingCalendar = () => {
	const { t, i18n } = useTranslation()
	const locale = i18n.language === 'is' ? is : enGB
	const { isStaff } = useUserRoles()
	const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(new Date()))
	const [query, setQuery] = useState<string | null>(
		`year=${getYear(new Date())}&month=${getMonth(new Date()) + 1}`
	)
	const [items, setItems] = useState<BookingFormattedInfo[]>([])
	const [chosenDate, setChosenDate] = useState<Date | null>(null)
	const { isOpen, openModal, closeModal } = useModal()

	const { bookings } = useFetchBookings(query as string)

	usePopulateCalendar({ data: bookings?.data, setItems })
	useSetQuery({ currentMonth, setQuery })

	const days = useMemo<(DayCounts | null)[]>(() => {
		const first = startOfMonth(currentMonth)
		const last = endOfMonth(currentMonth)
		const cells: (DayCounts | null)[] = Array.from({ length: getDay(first) }, () => null)

		for (let date = first; date <= last; date = addDays(date, 1)) {
			const dayItems = items.filter((item) => item.date && isSameDay(item.date, date))

			cells.push({
				date,
				starting: dayItems.filter((item) => item.title === 'start').length,
				ending: dayItems.filter((item) => item.title === 'end').length,
				takedown: dayItems.filter((item) => item.takedownRequested).length
			})
		}

		while (cells.length % 7 !== 0) cells.push(null)

		return cells
	}, [currentMonth, items])

	const weekdays = useMemo(
		() =>
			Array.from({ length: 7 }, (_, index) =>
				format(addDays(new Date(2023, 0, 1), index), 'EEEE', { locale })
			),
		[locale]
	)

	const chosenItems = chosenDate
		? items.filter((item) => item.date && isSameDay(item.date, chosenDate))
		: []
	const startingItems = chosenItems.filter((item) => item.title === 'start')
	const endingItems = chosenItems.filter((item) => item.title === 'end')

	const openDay = (date: Date) => {
		setChosenDate(date)
		openModal()
	}

	const renderBooking = (item: BookingFormattedInfo) => (
		<li key={`${item.title}-${item.id}`} className="min-w-0">
			<div className="flex items-baseline justify-between gap-2">
				<Link
					className="text-[13px] font-bold tabular-nums text-primary hover:underline"
					to={routes.bookingEdit(String(item.id))}>
					{item.ref}
				</Link>
				<span className="flex min-w-0 items-baseline gap-1 text-[12px] font-semibold text-foreground">
					<span className="truncate">{item.boothType}</span>
					{item.boothNumber && <span className="shrink-0 tabular-nums">{item.boothNumber}</span>}
				</span>
			</div>
			<p className="truncate text-[12px] font-medium text-foreground/90">{item.user}</p>
			<p className="text-[11px] tabular-nums text-muted-foreground">
				{item.start_date ? format(new Date(item.start_date), 'dd.MM.yyyy') : '-'} -{' '}
				{item.end_date ? format(new Date(item.end_date), 'dd.MM.yyyy') : '-'}
			</p>
			{(item.status || item.completed || item.overdue) && (
				<div className="mt-1 flex flex-wrap items-center gap-1">
					{item.status ? (
						<BookingStatus dot={false} className={tagClassName} status={item.status} />
					) : (
						item.completed && (
							<Badge dot={false} variant="muted" className={tagClassName}>
								{t('Completed')}
							</Badge>
						)
					)}
					{item.overdue && (
						<Badge dot={false} variant="warning" className={tagClassName}>
							{t('Overdue')}
						</Badge>
					)}
				</div>
			)}
			{item.takedownRequested && (
				<p className="mt-0.5 text-[11px] font-semibold text-warning">{t('Takedown requested')}</p>
			)}
		</li>
	)

	return (
		<>
			<Header
				title={t('Bookings')}
				actions={isStaff && <CtaLink to={routes.bookingAdd} text={t('Add booking')} compact />}
			/>
			<div className="px-5 lg:px-0">
				<div className="overflow-hidden rounded-lg border border-border bg-card shadow-panel">
					<div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
						<CtaLink
							to={routes.bookingIndex}
							text={t('Back to list')}
							icon={<ArrowLeftIcon />}
							variant="outline"
						/>
						<div className="flex items-center justify-between gap-2 sm:justify-start">
							<IconButton
								aria-label={t('Previous')}
								onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
								<ChevronLeftIcon />
							</IconButton>
							<span className="min-w-[160px] text-center font-display text-[15px] font-bold capitalize text-foreground">
								{format(currentMonth, 'LLLL yyyy', { locale })}
							</span>
							<IconButton
								aria-label={t('Next')}
								onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
								<ChevronRightIcon />
							</IconButton>
						</div>
					</div>
					<div className="overflow-x-auto">
						<div className="min-w-[980px]">
							<div className="grid grid-cols-7 border-b border-border bg-muted/50">
								{weekdays.map((weekday) => (
									<div
										key={weekday}
										className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
										{weekday}
									</div>
								))}
							</div>
							<div className="grid grid-cols-7">
								{days.map((day, index) => {
									const hasBookings = !!day && day.starting + day.ending > 0
									const chips = day
										? [
												{ key: 'starting', count: day.starting, variant: 'success' as const },
												{ key: 'ending', count: day.ending, variant: 'danger' as const },
												{ key: 'takedown', count: day.takedown, variant: 'warning' as const }
											].filter((chip) => chip.count > 0)
										: []

									return (
										<div
											key={day ? day.date.toISOString() : `empty-${index}`}
											role={hasBookings ? 'button' : undefined}
											tabIndex={hasBookings ? 0 : undefined}
											aria-label={
												hasBookings && day
													? t('Show bookings for {{ date }}', {
															date: format(day.date, 'd MMMM', { locale })
														})
													: undefined
											}
											className={cn(
												'min-h-[120px] border-b border-r border-border p-2 text-left',
												hasBookings &&
													'cursor-pointer transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
												day && isToday(day.date) && 'bg-primary-soft/50'
											)}
											onClick={hasBookings && day ? () => openDay(day.date) : undefined}
											onKeyDown={
												hasBookings && day
													? (event) => {
															if (event.key === 'Enter' || event.key === ' ') {
																event.preventDefault()
																openDay(day.date)
															}
														}
													: undefined
											}>
											{day && (
												<>
													<div
														className={cn(
															'mb-2 inline-grid h-6 w-6 place-items-center rounded-md text-[12px] font-bold tabular-nums',
															isToday(day.date)
																? 'bg-primary text-primary-foreground'
																: 'text-muted-foreground'
														)}>
														{format(day.date, 'd')}
													</div>
													<div className="flex flex-col items-stretch">
														{chips.map((chip, chipIndex) => (
															<div key={chip.key}>
																{chipIndex > 0 && <div className="h-px w-full bg-border" />}
																<div className="py-0.5">
																	<Badge
																		variant={chip.variant}
																		className="py-0.5 font-bold tabular-nums">
																		{chip.count}
																	</Badge>
																</div>
															</div>
														))}
													</div>
												</>
											)}
										</div>
									)
								})}
							</div>
						</div>
					</div>
					<div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border px-4 py-3 text-[11px] font-medium text-muted-foreground">
						<span className="flex items-center gap-1.5">
							<span className={cn(legendDot, 'bg-success')} /> {t('In')}
						</span>
						<span aria-hidden="true" className="h-3.5 w-px bg-border" />
						<span className="flex items-center gap-1.5">
							<span className={cn(legendDot, 'bg-danger')} /> {t('Out')}
						</span>
						<span aria-hidden="true" className="h-3.5 w-px bg-border" />
						<span className="flex items-center gap-1.5">
							<span className={cn(legendDot, 'bg-warning')} /> {t('Takedown requested')}
						</span>
					</div>
				</div>
			</div>

			<Dialog
				isOpened={isOpen}
				ariaLabel={
					chosenDate
						? t('Bookings on {{ date }}', { date: format(chosenDate, 'd MMMM', { locale }) })
						: ''
				}
				className="max-w-xl"
				closeModal={closeModal}>
				<div className="flex items-center justify-between gap-3 px-6 pb-4 pt-6">
					<h2 className="font-display text-lg font-bold tracking-tight text-foreground">
						{chosenDate ? format(chosenDate, 'd MMMM', { locale }) : ''}
					</h2>
					<button
						type="button"
						aria-label={t('Close')}
						className="grid h-9 w-9 place-items-center rounded-md text-foreground transition-colors hover:bg-muted"
						onClick={closeModal}>
						<XMarkIcon className="h-4 w-4 stroke-2" />
					</button>
				</div>
				<div className="grid max-h-[60vh] grid-cols-1 gap-6 overflow-y-auto px-6 pb-6 sm:grid-cols-2">
					<div className="min-w-0">
						<h3 className="font-display text-[13px] font-bold text-success">{t('In')}</h3>
						<ul className="mt-3 space-y-4">
							{startingItems.length ? (
								startingItems.map(renderBooking)
							) : (
								<li className="text-[12px] text-muted-foreground">{t('No bookings')}</li>
							)}
						</ul>
					</div>
					<div className="min-w-0">
						<h3 className="font-display text-[13px] font-bold text-danger">{t('Out')}</h3>
						<ul className="mt-3 space-y-4">
							{endingItems.length ? (
								endingItems.map(renderBooking)
							) : (
								<li className="text-[12px] text-muted-foreground">{t('No bookings')}</li>
							)}
						</ul>
					</div>
				</div>
				<div className="border-t border-border px-5 py-3 text-[11px] font-medium text-muted-foreground">
					<span className="flex items-center gap-1.5">
						<span className={cn(legendDot, 'bg-warning')} />
						{t('Yellow note means the booking requested takedown beforehand.')}
					</span>
				</div>
			</Dialog>
		</>
	)
}

export default BookingCalendar
